<template>
    <div class="venue-events">
        <div class="events-header">
            <div class="title-container">
                <h1>{{ $t('venue.events.headline') }}</h1>
                <Button
                    type="primary"
                    icon="plus"
                    :value="$t('venue.events.create_event')"
                    round
                    @click="$router.push({ name: 'event-new', params: { id: $route.params.id, venueId: venue.id } })"
                />
            </div>
            <div v-if="false" class="search-container">
                <div class="search">
                    <FeatherIcon name="search" />
                    <input type="text" placeholder="Events durchsuchen..." />
                </div>
                <div class="filter-container">
                    <div class="filter">
                        <div class="text copy-m">Anstehende Veranstaltungen</div>
                        <FeatherIcon name="chevron-down" />
                    </div>
                </div>
            </div>
        </div>
        <div v-show="!$apollo.loading && !events && events.length > 0" class="empty">
            <EmptyState
                :message="{
                    title: $t('venue.events.empty_state.title'),
                    description: $t('venue.events.empty_state.description'),
                }"
                type="events"
            >
                <Button
                    v-if="false"
                    :value="$t('venue.events.empty_state.button_connect_facebook')"
                    type="secondary"
                    round
                />
                <Button
                    :value="$t('venue.events.empty_state.button_create_event')"
                    type="primary"
                    icon="plus"
                    round
                    @click="$router.push({ name: 'event-new', params: { id: $route.params.id, venueId: venue.id } })"
                />
            </EmptyState>
        </div>
        <div v-if="!events" class="list loader">
            <Event v-for="event in new Array(5)" :key="event" :loading="true" />
        </div>
        <div v-if="events" class="list event-list">
            <Event
                v-for="event in events"
                :key="event.id"
                :data="event"
                :loading="$apollo.loading"
                @click="$router.push({ name: 'event', params: { event_id: event.id } })"
                @delete="openDeleteModal"
                @publish="publishEvent"
                @unpublish="unpublishEvent"
                @cancelEvent="cancelEvent"
            />
            <Modal
                v-if="deleteModalOpen"
                class="delete-event-modal"
                @cancel="deleteModalOpen = false"
                @confirm="deleteEvent"
            >
                <div class="modal-content">
                    <div class="headline-container">
                        <div class="copy-l bold">{{ $t('event_overview.delete_modal.delete_event') }}</div>
                    </div>
                    <div class="main">
                        <div class="copy-m">
                            {{ $t('event_overview.delete_modal.description') }}
                        </div>
                    </div>
                    <div class="action-container fullwidth">
                        <Button
                            type="secondary"
                            :value="$t('event_overview.delete_modal.cancel')"
                            @click="deleteModalOpen = false"
                        />
                        <Button type="red" :value="$t('event_overview.delete_modal.delete')" @click="deleteEvent" />
                    </div>
                </div>
            </Modal>
        </div>
    </div>
</template>

<script>
import Event from '@/components/Event'
import Button from '@/components/Button'
import FeatherIcon from '@/components/FeatherIcon'
import gql from 'graphql-tag'
import Modal from '@/components/Modal'
import EmptyState from '@/components/EmptyState'

export default {
    name: 'Venue',
    components: {
        Event,
        Button,
        FeatherIcon,
        Modal,
        EmptyState,
    },
    data() {
        return {
            deleteModalOpen: false,
        }
    },
    apollo: {
        events: {
            query: gql`
                query ($filter: EventsFilter) {
                    events(limit: 25, filter: $filter, sortBy: Date) {
                        id
                        name
                        coverImageUrl
                        sourceAttendingCount
                        genres
                        sourceInterestedCount
                        startDate
                        endDate
                    }
                }
            `,
            variables() {
                return {
                    filter: { venueIds: [this.$route.params.id] },
                }
            },
        },
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        email
                        phone
                        websiteUrl
                        dresscode
                        name
                        logoUrl
                        genres
                        priceLevel
                        capacity
                        activeEvents {
                            id
                            capacityUsage
                            capacityInUse
                        }
                    }
                }
            `,
            pollInterval: 5000,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    computed: {
        venueCapacityUsage() {
            let capacityUsage = 0
            if (this.venue) {
                for (let activeEvent of this.venue.activeEvents) {
                    capacityUsage = capacityUsage + activeEvent.capacityInUse
                }
            }
            return capacityUsage
        },
    },
    methods: {
        kFormatter(num) {
            return Math.abs(num) > 999
                ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(1) + 'k'
                : Math.sign(num) * Math.abs(num)
        },
        async unpublishEvent(event) {
            event.status = 'Draft'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: event.id,
                        value: { status: 'Draft' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.event_could_not_be_unpublished'))
                })
        },
        async cancelEvent(event) {
            event.status = 'Canceled'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: event.id,
                        value: { status: 'Canceled' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.event_could_not_be_canceled'))
                })
        },
        async publishEvent(event) {
            event.status = 'Published'
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ($id: ID!, $value: PartialEventInput!) {
                        updateEvent(id: $id, input: $value) {
                            status
                        }
                    }
                `,
                variables: {
                    id: event.id,
                    value: { status: 'Published' },
                },
            })
        },
        openDeleteModal() {
            this.deleteModalOpen = true
        },
        async deleteEvent() {
            this.deleteModalOpen = false
        },
    },
}
</script>

<style scoped lang="sass">
.venue-events
    .delete-event-modal ::v-deep
        .modal-container
            top: 30%

        .modal-content
            max-width: 348px

    .events-header
        margin-bottom: $xl

        .title-container
            margin-bottom: $l

        .search-container
            border-bottom: 1px solid $gray-1
</style>
